/**
 * Every React Query key in the application, in one module.
 *
 * They started out beside the hooks that read them, which was fine until two
 * modules needed each other's keys: approving a registration invalidates the
 * volunteer lists, and deleting a volunteer invalidates the submissions panel.
 * That is a genuine circular import — `useRegistrations` needing
 * `useVolunteers` needing `useRegistrations` — and with `const` objects
 * evaluated at module load, whichever side loses the race gets `undefined`, so
 * a cache silently stops invalidating.
 *
 * Keys have no dependencies of their own, so lifting them out breaks the cycle
 * for good. The hooks re-export their own registry, so callers do not need to
 * know this file exists.
 */

export const volunteerKeys = {
  all: ['volunteers'] as const,
  list: (params: Record<string, unknown>) => ['volunteers', 'list', params] as const,
  detail: (id: string) => ['volunteers', 'detail', id] as const,
  assignments: (id: string) => ['volunteers', 'detail', id, 'assignments'] as const,
  attendance: (id: string) => ['volunteers', 'detail', id, 'attendance'] as const,
  registrations: (id: string) => ['volunteers', 'detail', id, 'registrations'] as const,
  history: (id: string) => ['volunteers', 'detail', id, 'history'] as const,
};

export const registrationKeys = {
  all: ['registrations'] as const,
  list: (params: Record<string, unknown>) => ['registrations', 'list', params] as const,
  detail: (id: string) => ['registrations', 'detail', id] as const,
  awaitingDecision: ['registrations', 'awaiting-decision'] as const,
};

export const programKeys = {
  all: ['programs'] as const,
  list: (params: Record<string, unknown>) => ['programs', 'list', params] as const,
  detail: (id: string) => ['programs', 'detail', id] as const,
};

export const activityKeys = {
  all: ['activities'] as const,
  list: (params: Record<string, unknown>) => ['activities', 'list', params] as const,
  detail: (id: string) => ['activities', 'detail', id] as const,
};

export const assignmentKeys = {
  all: ['assignments'] as const,
  forActivity: (activityId: string) => ['assignments', 'activity', activityId] as const,
};

export const attendanceKeys = {
  all: ['attendance'] as const,
  list: (params: Record<string, unknown>) => ['attendance', 'list', params] as const,
  forActivity: (activityId: string) => ['attendance', 'activity', activityId] as const,
  history: (id: string) => ['attendance', 'detail', id, 'history'] as const,
};

export const organizationKeys = {
  all: ['organizations'] as const,
  list: (params: Record<string, unknown>) => ['organizations', 'list', params] as const,
};

export const referenceKeys = {
  all: ['reference-values'] as const,
  byCategory: (category: string) => ['reference-values', category] as const,
  grouped: ['reference-values', 'grouped'] as const,
};

export const metricsKeys = {
  all: ['metrics'] as const,
  /*
   * The range is part of the key. Two windows are two different answers, and a
   * shared key would serve last quarter's figures under this quarter's caption
   * until the refetch landed.
   */
  dashboard: (from = '', to = '') => ['metrics', 'dashboard', from, to] as const,
};

export const exportKeys = {
  all: ['exports'] as const,
  /*
   * The window is part of the key, like the dashboard's. A preview is a claim
   * about a specific file; serving last quarter's rows under this quarter's
   * caption would be the one failure this screen exists to prevent.
   */
  preview: (dataset: string, from = '', to = '') =>
    ['exports', 'preview', dataset, from, to] as const,
};

export const certificateKeys = {
  all: ['certificates'] as const,
  /** The admin table: live figures per volunteer. */
  summaries: (params: Record<string, unknown>) => ['certificates', 'summaries', params] as const,
  /** One volunteer's itemised activities, loaded when a row is expanded. */
  activities: (volunteerId: string) =>
    ['certificates', 'summaries', volunteerId, 'activities'] as const,
  /** The issue log. */
  list: (params: Record<string, unknown>) => ['certificates', 'list', params] as const,
};
